// SPR equivalence is decided in ONE place: lib/test-engine/spr-answer.ts.
// scoring.ts used to carry its own copy of parseSprNumber/gradeSpr; both moved
// there so the practice/drill/review surfaces and the SAT engine cannot drift
// apart again. Re-exported here so existing importers keep working unchanged.
import { parseSprNumber, gradeSpr } from '@/lib/test-engine/spr-answer'

export { parseSprNumber, gradeSpr }

/**
 * Pure scoring and routing utilities for the SAT test engine.
 * Extracted from finish-module and finish-test route handlers.
 */

/** Difficulty weight: H=1.25, M=1.1, E/other=1.0 */
export function difficultyWeight(d: string | null | undefined): number {
  if (d === 'H') return 1.25
  if (d === 'M') return 1.1
  return 1.0
}

/**
 * Per-route section-score caps. The Digital SAT is multistage adaptive: a
 * student routed to the easier Module 2 cannot reach the top of the 200-800
 * range even with a perfect Module 2 (College Board confirms an 800 section
 * score can become unattainable after Module 1 misses). Only the hard route
 * spans the full range, so its cap is a no-op — hard-route scoring is
 * unchanged. Config-driven so the caps are easy to recalibrate.
 */
export const ROUTE_SECTION_CAP: Record<'E' | 'M' | 'H', number> = {
  E: 590,
  M: 700,
  H: 800,
}

// Real raw-score -> section-score conversion (midpoint of the published
// lower/upper range), from College Board "Scoring Your Paper SAT Practice
// Test #4". Indexed by that test's raw count: RW 0..66, Math 0..54. We look
// these up by PERCENT correct (the engine's adaptive form has different
// question counts), so only the curve SHAPE matters — which is the point: the
// old synthetic 200+600*pct^0.9 curve overshot this real curve by 30-77 pts in
// the mid-range. Source: satsuite.collegeboard.org/media/pdf/scoring-sat-practice-test-4-digital.pdf
const RW_CONCORDANCE: number[] = [
  200, 200, 200, 200, 200, 200, 200, 205, 210, 220, 240, 250, 260, 270, 290, 300, 330, 350, 360,
  370, 380, 380, 390, 400, 410, 420, 430, 430, 440, 450, 460, 470, 470, 480, 490, 500, 500, 510,
  520, 530, 540, 550, 550, 560, 570, 580, 590, 600, 600, 610, 620, 630, 640, 640, 650, 660, 670,
  680, 690, 700, 710, 720, 730, 740, 760, 780, 795,
]
const MATH_CONCORDANCE: number[] = [
  200, 200, 200, 200, 200, 200, 200, 210, 215, 235, 265, 295, 305, 315, 325, 335, 345, 345, 355,
  365, 375, 385, 385, 395, 405, 415, 435, 445, 455, 475, 485, 495, 515, 525, 535, 545, 565, 575,
  585, 595, 605, 615, 635, 645, 665, 685, 705, 725, 745, 755, 765, 775, 785, 790, 795,
]

export type ScaleCurve = 'rw' | 'math' | 'legacy'

/**
 * Interpolate a section score from a concordance table at a given percent
 * correct. The table is indexed by raw count (its own max), so `pct` maps to a
 * fractional row position and we linearly interpolate between adjacent rows.
 */
function scaleFromConcordance(pct: number, table: number[]): number {
  const maxRaw = table.length - 1
  const x = Math.max(0, Math.min(1, pct)) * maxRaw
  const lo = Math.floor(x)
  const hi = Math.min(lo + 1, maxRaw)
  return table[lo] + (table[hi] - table[lo]) * (x - lo)
}

/**
 * Scale a weighted score to the SAT 200-800 range, rounded to the nearest 10.
 * `curve` selects the real CB conversion for that section ('rw'/'math'); the
 * 'legacy' default keeps the old synthetic curve for callers without section
 * context. `cap` bounds the top of the range for adaptive routing (an easy/
 * medium Module-2 route can't reach 800); it's applied AFTER the curve, so it
 * acts as a ceiling rather than compressing the whole range.
 */
export function scaleScore(
  rawWeighted: number,
  maxWeighted: number,
  cap = 800,
  curve: ScaleCurve = 'legacy',
): number {
  if (maxWeighted <= 0) return 200
  const pct = Math.max(0, Math.min(1, rawWeighted / maxWeighted))
  let base: number
  if (curve === 'rw') base = scaleFromConcordance(pct, RW_CONCORDANCE)
  else if (curve === 'math') base = scaleFromConcordance(pct, MATH_CONCORDANCE)
  else base = 200 + 600 * Math.pow(pct, 0.9) // legacy synthetic fallback
  const rounded = Math.round(base / 10) * 10
  return Math.min(cap, Math.max(200, rounded))
}

/**
 * Determine M2 route bin based on M1 performance.
 *
 * When `weightedScore` is provided, uses adjusted thresholds that account
 * for question difficulty (harder questions answered correctly count more).
 * Falls back to raw correct-answer count thresholds otherwise.
 */
/**
 * SAT M1 weighted-score routing thresholds, exported so report surfaces can
 * state the margin honestly ("2.4 weighted points from the Hard module")
 * instead of duplicating these literals. routeBin() below is the only
 * decision-maker; this constant is the single source of the numbers.
 *
 * RW M1: 25 OPERATIONAL questions (27 served − 2 pretest, #783), max weighted
 * ~28 — rescaled from the pre-pretest 27-item values (24/16) by 25/27.
 * MATH M1: 20 operational (22 served − 2 pretest), max weighted ~22 —
 * rescaled from the 22-item values (19/12) by 20/22.
 */
export const SAT_M1_ROUTE_THRESHOLDS: Record<'RW' | 'MATH', { H: number; M: number }> = {
  RW: { H: 22, M: 15 },
  MATH: { H: 17, M: 11 },
}

export function routeBin(
  section: 'RW' | 'MATH',
  rights: number,
  _totalQuestions?: number,
  weightedScore?: number,
): 'E' | 'M' | 'H' {
  if (weightedScore !== undefined) {
    const t = SAT_M1_ROUTE_THRESHOLDS[section]
    return weightedScore >= t.H ? 'H' : weightedScore >= t.M ? 'M' : 'E'
  }

  // Fallback: raw count thresholds (legacy path — finish-module always passes
  // weightedScore, so this is currently unused; left at the pre-pretest values).
  if (section === 'RW') {
    return rights >= 20 ? 'H' : rights >= 14 ? 'M' : 'E'
  }
  return rights >= 16 ? 'H' : rights >= 10 ? 'M' : 'E'
}

/**
 * Route bin for the shorter CHECKPOINT modules (13 RW / 10 Math, no pretest).
 * The SAT routeBin thresholds are tuned to 25/20 operational items and would
 * route almost everyone to 'E' at this size, so we apply the SAME RELATIVE
 * thresholds expressed as a fraction of the all-hard weighted ceiling
 * (operationalCount × 1.25). SAT-derived fractions: RW ~0.78/0.54, Math
 * ~0.77/0.50.
 */
export function checkpointRouteBin(
  section: 'RW' | 'MATH',
  weightedScore: number,
  operationalCount: number,
): 'E' | 'M' | 'H' {
  const maxWeighted = operationalCount * difficultyWeight('H')
  const frac = maxWeighted > 0 ? weightedScore / maxWeighted : 0
  // H-route threshold lowered so a genuinely strong Module-1 performance reliably
  // earns the HARD second module (raising the challenge/ceiling for top students).
  // M/E thresholds unchanged, so mid/lower performers route exactly as before.
  if (section === 'RW') return frac >= 0.73 ? 'H' : frac >= 0.54 ? 'M' : 'E'
  return frac >= 0.72 ? 'H' : frac >= 0.5 ? 'M' : 'E'
}

/**
 * Confidence band (± scaled points) for a total estimate from a SHORT test.
 * Fewer scored items ⇒ wider band. Heuristic (band ∝ 1/√items), bounded to
 * [20, 80] and rounded to 10s: ≈ ±40 for a ~46-item Checkpoint, ≈ ±30 for a
 * full ~98-item SAT. Honest "give or take", not a rigorous IRT SEM — we don't
 * have per-item calibration, so this just conveys "shorter test = less certain".
 */
export function scoreBand(scoredItems: number): number {
  if (scoredItems <= 0) return 100
  const raw = 270 / Math.sqrt(scoredItems)
  return Math.min(80, Math.max(20, Math.round(raw / 10) * 10))
}

/** Fisher-Yates in-place shuffle. Returns the same array reference. */
export function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

/**
 * Pick the user-facing score for an attempt.
 *
 * The SAT scoring pipeline writes the scaled 400–1600 score to `totalScaled`
 * (see /api/sat/finish-test) and leaves the legacy `score` field null. The
 * old `score` field is still used by some non-SAT attempt types (drills) and
 * by older attempts that pre-date the totalScaled column.
 *
 * Always prefer totalScaled when present; fall back to score; default 0.
 */
export function displayScore(attempt: {
  totalScaled?: number | null
  score?: number | null
}): number {
  if (attempt.totalScaled != null) return Math.round(attempt.totalScaled)
  if (attempt.score != null) return Math.round(attempt.score)
  return 0
}

/**
 * Compute SAT-style scaled scores for a completed attempt.
 *
 * Single source of truth for the SAT scaled-score MATH. The shared finalize
 * substrate (src/lib/test-engine/attempt-finalize.ts) is the sole production
 * caller: both test-taking engines — the adaptive SAT engine (finish-module)
 * and the legacy fixed-form engine (attempts/submit) — finalize through it, so
 * the route-cap reading + score write can no longer drift between them (#733).
 *
 * Returns null if the attempt isn't SAT-shaped (no items in either RW or
 * MATH section). Callers can detect this and fall back to legacy scoring.
 */
export type AttemptScoreItem = {
  correct: boolean | null
  section: string | null // expected: "RW" | "MATH"
  questionId: string
  /**
   * Pretest/equating items (#783) are unscored. Absent or true = scored
   * (back-compatible with historical items that predate the flag); only an
   * explicit false excludes the item from raw count, weighted numerator, and
   * the scaled denominator.
   */
  scored?: boolean
}

export type AttemptScoreQuestionMeta = {
  id: string
  difficulty: string | null
}

export type AttemptScores = {
  rwRaw: number
  mathRaw: number
  rwScaled: number
  mathScaled: number
  totalScaled: number
}

export function computeAttemptScores(
  items: AttemptScoreItem[],
  questionMeta: AttemptScoreQuestionMeta[],
  routes?: { rw?: 'E' | 'M' | 'H' | null; math?: 'E' | 'M' | 'H' | null },
): AttemptScores | null {
  const meta = new Map(questionMeta.map((q) => [q.id, q]))

  let rwW = 0,
    rwMax = 0,
    mathW = 0,
    mathMax = 0,
    rwRaw = 0,
    mathRaw = 0

  for (const it of items) {
    // Pretest/equating items are unscored — excluded from the raw count, the
    // weighted numerator, AND the scaled denominator together so pct stays
    // correct (#783). Absent/true = scored.
    if (it.scored === false) continue
    const m = meta.get(it.questionId)
    const w = difficultyWeight(m?.difficulty)
    if (it.section === 'RW') {
      rwMax += w
      if (it.correct) {
        rwW += w
        rwRaw += 1
      }
    } else if (it.section === 'MATH') {
      mathMax += w
      if (it.correct) {
        mathW += w
        mathRaw += 1
      }
    }
  }

  // Not SAT-shaped — caller should handle differently (legacy score, etc.)
  if (rwMax === 0 || mathMax === 0) return null

  const rwCap = routes?.rw ? ROUTE_SECTION_CAP[routes.rw] : 800
  const mathCap = routes?.math ? ROUTE_SECTION_CAP[routes.math] : 800
  const rwScaled = scaleScore(rwW, rwMax, rwCap, 'rw')
  const mathScaled = scaleScore(mathW, mathMax, mathCap, 'math')
  const totalScaled = rwScaled + mathScaled

  return { rwRaw, mathRaw, rwScaled, mathScaled, totalScaled }
}

/**
 * 0.6 single-section tests: the 200–800 scaled score for ONE section, using the
 * exact same weighting + route-cap math as `computeAttemptScores` (which is left
 * untouched — it still returns null for a one-section attempt, and every total-
 * score surface keys off a null `totalScaled`, so single-section tests stay out
 * of total history/leaderboard/college-band automatically). Returns null when
 * the section has no scored items.
 */
export function computeSectionScaled(
  items: AttemptScoreItem[],
  questionMeta: AttemptScoreQuestionMeta[],
  section: 'RW' | 'MATH',
  routeCap?: 'E' | 'M' | 'H' | null,
): { raw: number; scaled: number } | null {
  const meta = new Map(questionMeta.map((q) => [q.id, q]))
  let weighted = 0,
    max = 0,
    raw = 0
  for (const it of items) {
    if (it.scored === false) continue // pretest/equating items excluded (#783)
    if (it.section !== section) continue
    const w = difficultyWeight(meta.get(it.questionId)?.difficulty)
    max += w
    if (it.correct) {
      weighted += w
      raw += 1
    }
  }
  if (max === 0) return null
  const cap = routeCap ? ROUTE_SECTION_CAP[routeCap] : 800
  return { raw, scaled: scaleScore(weighted, max, cap, section === 'RW' ? 'rw' : 'math') }
}

/** Live preview state for an SPR (grid-in) input. */
export type SprPreview = { kind: 'empty' } | { kind: 'invalid' } | { kind: 'value'; value: string }

/**
 * Build a live preview of how an SPR input string will be interpreted by the
 * grader (#786). Reuses parseSprNumber so the preview can never disagree with
 * what gradeSpr actually accepts. Returns `empty` for blank input, `invalid`
 * for a non-empty string the grader can't parse, and the interpreted numeric
 * value (float noise trimmed) otherwise.
 */
export function formatSprPreview(raw: string | null | undefined): SprPreview {
  const t = (raw ?? '').trim()
  if (t === '') return { kind: 'empty' }
  const n = parseSprNumber(t)
  if (n === null) return { kind: 'invalid' }
  return { kind: 'value', value: parseFloat(n.toFixed(6)).toString() }
}

/**
 * Grade a single answer against its key — the one correctness rule both
 * test-taking engines must share. MCQ compares the trimmed choice label; SPR
 * (grid-in) uses gradeSpr for numeric equivalence (.6 == 3/5). A numericAnswer
 * takes precedence over a choice when both are present (SPR items).
 *
 * Returns undefined when the question is effectively ungraded (no response, or
 * no answer key) so callers can distinguish "unanswered" from "wrong".
 *
 * Extracted from /api/sat/save so the legacy fixed-form engine
 * (/api/attempts/submit, which runs ad-hoc drills) grades identically instead
 * of its old raw `answer === choice` (no trim, no SPR) — see #767.
 */
export function gradeAnswer(
  answerKey: string | null | undefined,
  response: { choice?: string | null; numericAnswer?: string | null },
): boolean | undefined {
  let correct: boolean | undefined = undefined
  if (response.choice != null) {
    correct = answerKey ? response.choice.trim() === answerKey.trim() : undefined
  }
  if (response.numericAnswer != null && answerKey != null) {
    correct = gradeSpr(response.numericAnswer, answerKey)
  }
  return correct
}
